import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { PublicLayout } from "../components/layout/PublicLayout";
import { useAuth } from "../contexts/AuthContext";

export default function AuthRedirect() {
  const { user, loginWithGoogle, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [status, setStatus] = useState<"processing" | "error">("processing");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function handleAuth() {
      try {
        const searchParams = new URLSearchParams(location.search);
        const hashParams = new URLSearchParams(location.hash.substring(1));

        const idToken =
          searchParams.get("credential") ||
          searchParams.get("id_token") ||
          hashParams.get("credential") ||
          hashParams.get("id_token") ||
          searchParams.get("token");

        if (idToken) {
          const loggedInUser = await loginWithGoogle(idToken);
          if (isMounted) {
            navigate(
              loggedInUser.onboarded
                ? loggedInUser.role === "company"
                  ? "/company"
                  : "/app"
                : "/onboarding",
              { replace: true }
            );
          }
          return;
        }

        if (user) {
          if (isMounted) {
            navigate(
              user.onboarded
                ? user.role === "company"
                  ? "/company"
                  : "/app"
                : "/onboarding",
              { replace: true }
            );
          }
          return;
        }

        await refresh();
      } catch (err) {
        if (isMounted) {
          setStatus("error");
          setErrorMessage(
            err instanceof Error ? err.message : "An error occurred during authentication."
          );
        }
      }
    }

    handleAuth();

    return () => {
      isMounted = false;
    };
  }, [location, loginWithGoogle, navigate, refresh, user]);

  return (
    <PublicLayout>
      <div className="flex flex-col items-center justify-center px-6 py-20 text-center">
        {status === "processing" ? (
          <>
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-teal border-t-transparent" />
            <h1 className="mt-6 text-lg font-bold text-navy">Signing you in...</h1>
            <p className="mt-2 text-sm text-slate-500">Please wait a moment.</p>
          </>
        ) : (
          <>
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-coral/10 text-coral">
              ⚠️
            </div>
            <h1 className="mt-6 text-lg font-bold text-navy">Authentication Failed</h1>
            <p className="mt-2 text-sm text-slate-500">{errorMessage}</p>
            <button
              onClick={() => navigate("/login", { replace: true })}
              className="mt-6 rounded-full bg-navy px-6 py-2.5 text-sm font-semibold text-white shadow hover:bg-navy/90"
            >
              Go to Login
            </button>
          </>
        )}
      </div>
    </PublicLayout>
  );
}
